import { getSupabaseClient } from "./getSupabaseClient";

const MAX_RPC_BATCH_SIZE = 5000;
const DEFAULT_MAX_ROWS = 50000;

export async function getVehicleByUrl(url) {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("get_vehicle_by_url", {
    p_url: url,
  });
  if (error) {
    throw error;
  }
  return data?.[0] || null;
}

export async function getRowsByMakeModelYearRange({
  make,
  model,
  yearLow = null,
  yearHigh = null,
  limit = MAX_RPC_BATCH_SIZE,
  offset = 0,
  soldOnly = false,
}) {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc(
    "get_rows_by_make_model_year_range",
    {
      p_make: make,
      p_model: model,
      p_year_low: yearLow,
      p_year_high: yearHigh,
      p_limit: limit,
      p_offset: offset,
      p_sold_only: soldOnly,
    }
  );
  if (error) {
    throw error;
  }
  return data || [];
}

export async function getAllRowsByMakeModelYearRange({
  make,
  model,
  yearLow = null,
  yearHigh = null,
  soldOnly = false,
  maxRows = DEFAULT_MAX_ROWS,
  batchSize = MAX_RPC_BATCH_SIZE,
}) {
  const rows = [];
  let offset = 0;

  while (rows.length < maxRows) {
    const batch = await getRowsByMakeModelYearRange({
      make,
      model,
      yearLow,
      yearHigh,
      soldOnly,
      limit: Math.min(batchSize, MAX_RPC_BATCH_SIZE),
      offset,
    });

    if (batch.length === 0) {
      break;
    }

    rows.push(...batch);

    if (batch.length < batchSize) {
      break;
    }

    offset += batch.length;
  }

  return rows;
}

export async function getAvgByAuctionYear({
  make,
  model,
  includeRnm = true,
}) {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("get_avg_by_auction_year", {
    p_make: make,
    p_model: model,
    p_include_rnm: includeRnm,
  });
  if (error) {
    throw error;
  }
  return data || [];
}

export async function getAvgByYearRange({
  make,
  model,
  yearLow,
  yearHigh,
  includeRnm = true,
}) {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("get_avg_by_year_range", {
    p_make: make,
    p_model: model,
    p_year_low: yearLow,
    p_year_high: yearHigh,
    p_include_rnm: includeRnm,
  });
  if (error) {
    throw error;
  }
  return data?.[0] || null;
}

export async function getYoyDeltas({
  make,
  model,
  prevYear,
  currYear,
  includeRnm = true,
}) {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("get_yoy_deltas", {
    p_make: make,
    p_model: model,
    p_prev_year: prevYear,
    p_curr_year: currYear,
    p_include_rnm: includeRnm,
  });
  if (error) {
    throw error;
  }
  return data?.[0] || { delta_price: 0, delta_mileage: 0 };
}

export async function getLowHighMileagePriceAvgs({
  make,
  model,
  yearLow,
  yearHigh,
  includeRnm = true,
}) {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc(
    "get_low_high_mileage_price_avgs",
    {
      p_make: make,
      p_model: model,
      p_year_low: yearLow,
      p_year_high: yearHigh,
      p_include_rnm: includeRnm,
    }
  );
  if (error) {
    throw error;
  }
  return data?.[0] || {
    avg_mileage: 0,
    low_mileage_avg_price: 0,
    high_mileage_avg_price: 0,
  };
}

export async function getBestWorstModelYear({
  make,
  model,
  yearLow = null,
  yearHigh = null,
  includeRnm = true,
}) {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("get_best_worst_model_year", {
    p_make: make,
    p_model: model,
    p_year_low: yearLow,
    p_year_high: yearHigh,
    p_include_rnm: includeRnm,
  });
  if (error) {
    throw error;
  }
  return data?.[0] || {
    best_year: null,
    best_avg_price: 0,
    worst_year: null,
    worst_avg_price: 0,
  };
}

export async function getBestWorstColor({
  make,
  model,
  yearLow = null,
  yearHigh = null,
  includeRnm = true,
}) {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("get_best_worst_color", {
    p_make: make,
    p_model: model,
    p_year_low: yearLow,
    p_year_high: yearHigh,
    p_include_rnm: includeRnm,
  });
  if (error) {
    throw error;
  }
  return data?.[0] || {
    best_color: "N/A",
    best_avg_price: 0,
    worst_color: "N/A",
    worst_avg_price: 0,
  };
}

export async function getAvgPriceByColor({
  make,
  model,
  yearLow = null,
  yearHigh = null,
  includeRnm = true,
}) {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("get_avg_price_by_color", {
    p_make: make,
    p_model: model,
    p_year_low: yearLow,
    p_year_high: yearHigh,
    p_include_rnm: includeRnm,
  });
  if (error) {
    throw error;
  }
  return data || [];
}
